import React, { useState } from 'react';
import { TaskStatus, TaskStatusLabels, TaskStatusColors } from '../../types';
import { CameraIcon } from '../../components/icons/Icons';
import { FILE_PATTERNS } from '../../constants';
import { validateFile } from '../../lib/storageHelpers';

interface StatusChangeModalProps {
  isOpen: boolean;
  newStatus: TaskStatus | null;
  currentStatus: TaskStatus;
  onConfirm: (comment: string, photoFiles?: File[]) => Promise<{ success: boolean; error?: string }>;
  onClose: () => void;
}

interface FilePreview {
  name: string;
  dataUrl: string;
  file: File;
}

export const StatusChangeModal: React.FC<StatusChangeModalProps> = ({ isOpen, newStatus, currentStatus, onConfirm, onClose }) => {
  const [modalComment, setModalComment] = useState('');
  const [modalError, setModalError] = useState('');
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [filePreviews, setFilePreviews] = useState<FilePreview[]>([]);
  const [photoDragOver, setPhotoDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen || !newStatus) return null;

  /** El supervisor devuelve una tarea revisada al técnico. */
  const isReturn = currentStatus === 'IN_REVIEW' && newStatus === 'IN_PROGRESS';
  const commentRequired = newStatus === 'FINISHED' || newStatus === 'ISSUES';
  // Requisito original: foto al cerrar. Si la tarea viene de revisión, la
  // evidencia ya se entregó al enviarla, así que no se vuelve a pedir.
  const photosRequired =
    newStatus === 'IN_REVIEW' || (newStatus === 'FINISHED' && currentStatus !== 'IN_REVIEW');
  const photosShown = photosRequired || newStatus === 'ISSUES' || newStatus === 'FINISHED';

  const addFiles = (newFiles: File[]) => {
    const imageFiles = newFiles.filter(f => f.type.startsWith('image/'));
    const errors: string[] = [];
    const validFiles: File[] = [];
    imageFiles.forEach(file => {
      const err = validateFile(file, 'status_photo');
      if (err) errors.push(`${err.file}: ${err.reason}`);
      else validFiles.push(file);
    });
    setFileErrors(errors);
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setFilePreviews(prev => [...prev, { name: file.name, dataUrl: reader.result as string, file }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeFile = (idx: number) => {
    setFilePreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handlePhotoDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setPhotoDragOver(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handlePhotoPaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) files.push(file);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      addFiles(files);
    }
  };

  const handlePhotoBrowse = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = (ev: Event) => {
      const files = Array.from((ev.target as HTMLInputElement).files || []);
      if (files.length > 0) addFiles(files);
    };
    input.click();
  };

  const handleConfirm = async () => {
    if ((commentRequired || isReturn) && !modalComment.trim()) {
      setModalError(isReturn
        ? 'Explain what needs to be reworked before returning the task.'
        : 'A comment is required when changing to this status.');
      return;
    }
    if (photosRequired && filePreviews.length === 0) {
      setModalError(newStatus === 'IN_REVIEW'
        ? 'At least one photo is required to send the task for review.'
        : 'At least one completion photo is required when marking as Finished.');
      return;
    }
    const files = filePreviews.length > 0 ? filePreviews.map(fp => fp.file) : undefined;
    setModalError('');
    setSubmitting(true);
    // Wait for the server; only clear the form if the change actually stuck.
    const result = await onConfirm(modalComment.trim(), files);
    setSubmitting(false);
    if (!result.success) {
      setModalError(result.error || 'Could not change the task status. Please try again.');
      return;
    }
    setModalComment('');
    setModalError('');
    setFileErrors([]);
    setFilePreviews([]);
  };

  const handleClose = () => {
    setModalComment('');
    setModalError('');
    setFileErrors([]);
    setFilePreviews([]);
    onClose();
  };

  const isImage = (name: string) => FILE_PATTERNS.IMAGE.test(name);

  return (
    <div className="status-modal-backdrop" onClick={handleClose}>
      <div className="status-modal" onClick={e => e.stopPropagation()}>
        <div className="status-modal-header">
          <h4>Change Task Status</h4>
          <button className="status-modal-close" onClick={handleClose}>&times;</button>
        </div>
        <div className="status-modal-body">
          <div className="status-modal-new-value">
            <span className="status-modal-label">New Status:</span>
            <span
              className="status-modal-badge"
              style={{ backgroundColor: TaskStatusColors[newStatus], color: '#fff' }}
            >
              {TaskStatusLabels[newStatus]}
            </span>
          </div>

          <div className="status-modal-field">
            <label>
              Comment
              {(commentRequired || isReturn) && (
                <span className="required"> *</span>
              )}
            </label>
            <textarea
              value={modalComment}
              onChange={e => setModalComment(e.target.value)}
              onPaste={handlePhotoPaste}
              placeholder={
                isReturn
                  ? 'Describe what needs to be reworked...'
                  : newStatus === 'FINISHED'
                  ? 'Approval notes...'
                  : newStatus === 'IN_REVIEW'
                  ? 'Describe the work to review...'
                  : newStatus === 'ISSUES'
                  ? 'Describe the issue...'
                  : 'Optional comment...'
              }
              rows={3}
              autoFocus
            />
          </div>

          {/* Evidencia: obligatoria al enviar a revisión, opcional al reportar un problema */}
          {photosShown && (
            <div className="status-modal-field">
              <label>
                Photos {photosRequired ? <span className="required">*</span> : '(optional)'}
              </label>
              <div
                className={`photo-upload-area ${photoDragOver ? 'drag-over' : ''}`}
                onDrop={handlePhotoDrop}
                onDragOver={e => { e.preventDefault(); setPhotoDragOver(true); }}
                onDragLeave={() => setPhotoDragOver(false)}
                onPaste={handlePhotoPaste}
                tabIndex={0}
              >
                {filePreviews.length > 0 ? (
                  <div className="multi-files-preview">
                    {filePreviews.map((entry, idx) => (
                      <div key={idx} className="multi-file-item">
                        {isImage(entry.name) ? (
                          <img src={entry.dataUrl} alt={entry.name} className="multi-file-thumb" />
                        ) : (
                          <div className="multi-file-doc">
                            <span className="multi-file-doc-icon">&#128196;</span>
                            <span className="multi-file-doc-name">{entry.name.length > 20 ? entry.name.slice(0, 17) + '...' : entry.name}</span>
                          </div>
                        )}
                        <button type="button" className="multi-file-remove" onClick={() => removeFile(idx)} title="Remove">&times;</button>
                      </div>
                    ))}
                    <button type="button" className="multi-file-add-btn" onClick={handlePhotoBrowse} title="Add more photos">
                      <CameraIcon size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="photo-drop-content">
                    <CameraIcon size={24} />
                    <p className="photo-drop-text">Drag & drop, paste (Ctrl+V), or</p>
                    <button type="button" className="photo-upload-btn" onClick={handlePhotoBrowse}>
                      Browse Files
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {fileErrors.length > 0 && (
            <div className="status-modal-error">
              {fileErrors.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}
          {modalError && <div className="status-modal-error">{modalError}</div>}
        </div>
        <div className="status-modal-footer">
          <button className="status-modal-cancel" onClick={handleClose} disabled={submitting}>Cancel</button>
          <button className="status-modal-confirm" onClick={handleConfirm} disabled={submitting}>{submitting ? 'Saving…' : 'Confirm'}</button>
        </div>
      </div>
    </div>
  );
};
